<template>
  <div ref="root" class="tooltip-container">
    <slot name="tooltip">
      <TooltipComponent
        v-if="visible"
        :bbox="bbox ?? undefined"
        :margin="margin"
        :text="(text as string | number)"
        :position="position"
        :multiline="multiline"
        :multilineSmall="multilineSmall"
        :multilineManual="multilineManual"
      >
      </TooltipComponent>
    </slot>

    <slot></slot>
  </div>
</template>
<script setup lang="ts">
import {
  defineAsyncComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue'

const TooltipComponent = defineAsyncComponent(
  () => import('./tooltip-component/TooltipComponent.vue'),
)

defineOptions({ name: 'CartoComponentTooltip' })

const props = withDefaults(
  defineProps<{
    text?: string | number | boolean
    position?: string
    margin?: number
    multiline?: boolean
    multilineSmall?: boolean
    multilineManual?: boolean
  }>(),
  {
    position: 'top',
    multiline: false,
    multilineSmall: false,
    multilineManual: false,
  },
)

const root = ref<HTMLElement | null>(null)
const visible = ref(false)
const bbox = ref<DOMRect | null>(null)
const textValue = ref(props.text)

onMounted(() => {
  if (textValue.value && root.value) {
    root.value.addEventListener('mouseover', show, false)
    root.value.addEventListener('mouseout', hide, false)
  }
})

onBeforeUnmount(() => {
  if (root.value) {
    root.value.removeEventListener('mouseover', show)
    root.value.removeEventListener('mouseout', hide)
  }
  window.removeEventListener('scroll', hide)
})

function show(): void {
  if (root.value) {
    bbox.value = root.value.getBoundingClientRect()
  }
  visible.value = true
  window.addEventListener('scroll', hide, false)
}

function hide(): void {
  bbox.value = null
  visible.value = false
  window.removeEventListener('scroll', hide)
}

watch(
  () => props.text,
  () => {
    // Preserved from the original Options API component, which assigned the new
    // value to an undeclared property (`textValueValue`) and therefore had no
    // observable effect. Kept as a no-op to keep behaviour identical.
  },
)
</script>
<style scoped lang="scss" src="./tooltip.scss"></style>
